/**
 * category-filter.ts — client-side filter + sort for /tools/{category} pages.
 *
 * Server renders the full card list (SEO-complete); this only reorders/hides
 * already-present DOM nodes, so it degrades gracefully with JS off.
 *
 * Anchors:
 *   [data-category-filter]  — toolbar root
 *   #categoryFilterInput    — text filter
 *   #categoryTypeSelect     — tool-type filter (optional)
 *   #categorySortSelect     — sort order
 *   #categoryToolGrid       — the card grid
 *   [data-category-card]    — each card (data-title, data-searchable, data-tool-type)
 *   #categoryResultsTitle   — result count heading
 */
function t(key, fallback) {
    const translate = window.tnT;
    return typeof translate === "function" ? translate(key, fallback) : fallback;
}
function culture() {
    return window.__TN_CULTURE__ || "en-US";
}
export function formatCategoryToolCount(count, locale = culture()) {
    const category = new Intl.PluralRules(locale).select(count);
    const fallback = category === "one" ? "{0} tool" : "{0} tools";
    return t(`js.categoryFilter.toolCount.${category}`, fallback)
        .replace("{0}", new Intl.NumberFormat(locale).format(count));
}
(function initCategoryFilter() {
    "use strict";
    const toolbar = document.querySelector("[data-category-filter]");
    const gridMaybe = document.getElementById("categoryToolGrid");
    if (toolbar === null || gridMaybe === null)
        return;
    const grid = gridMaybe;
    const input = document.getElementById("categoryFilterInput");
    const typeSelect = document.getElementById("categoryTypeSelect");
    const sortSelect = document.getElementById("categorySortSelect");
    const countHeading = document.getElementById("categoryResultsTitle");
    const cards = Array.from(grid.querySelectorAll("[data-category-card]"));
    const originalOrder = cards.slice();
    function apply() {
        const query = (input?.value ?? "").trim().toLocaleLowerCase(culture());
        const type = typeSelect?.value ?? "";
        const sort = sortSelect?.value ?? "default";
        let visible = 0;
        for (const card of cards) {
            const haystack = card.dataset["searchable"] ?? "";
            const cardType = card.dataset["toolType"] ?? "";
            const matchesQuery = query === "" || haystack.includes(query);
            const matchesType = type === "" || cardType === type;
            const show = matchesQuery && matchesType;
            card.hidden = !show;
            if (show)
                visible += 1;
        }
        const ordered = originalOrder.slice();
        if (sort === "name-asc" || sort === "name-desc") {
            ordered.sort((a, b) => {
                const an = (a.dataset["title"] ?? "").toLowerCase();
                const bn = (b.dataset["title"] ?? "").toLowerCase();
                const comparison = new Intl.Collator(culture()).compare(an, bn);
                return sort === "name-asc" ? comparison : -comparison;
            });
            for (const card of ordered)
                grid.appendChild(card);
        }
        else {
            for (const card of originalOrder)
                grid.appendChild(card);
        }
        if (countHeading !== null) {
            // Guarded because the heading is a polite live region and this runs on
            // every settled keystroke. Narrowing "pd" to "pdf" often leaves the count
            // unchanged, and re-assigning the same string still replaces the text
            // node, which a live region reports as a change.
            const next = formatCategoryToolCount(visible);
            if (countHeading.textContent !== next) {
                countHeading.textContent = next;
            }
        }
        toggleEmptyState(visible === 0);
    }
    // The region is created once, empty, and left in the document for the page's
    // lifetime. It used to be built and inserted with its text already set, which
    // is the one shape a live region does not announce: assistive tech watches an
    // existing region for changes, so a node that arrives already populated is
    // read only if the user happens to navigate onto it. Toggling `hidden` has the
    // same defect, because a hidden node is outside the accessibility tree and
    // revealing it with content in place is indistinguishable from inserting it.
    // So the text is what toggles, and `:empty` in the page stylesheet is what
    // keeps the resting state from occupying its own margin.
    const emptyEl = document.createElement("p");
    emptyEl.className = "category-toolbar__empty";
    emptyEl.setAttribute("role", "status");
    grid.insertAdjacentElement("afterend", emptyEl);
    function toggleEmptyState(isEmpty) {
        const next = isEmpty ? t("js.categoryFilter.noMatches", "No tools match your filter.") : "";
        if (emptyEl.textContent !== next) {
            emptyEl.textContent = next;
        }
    }
    let debounce = 0;
    input?.addEventListener("input", () => {
        window.clearTimeout(debounce);
        debounce = window.setTimeout(apply, 120);
    });
    typeSelect?.addEventListener("change", apply);
    sortSelect?.addEventListener("change", apply);
})();
//# sourceMappingURL=category-filter.js.map